import { NgClass } from "@angular/common";
import { Component, computed, inject, signal, TemplateRef, ViewChild } from "@angular/core";
import { MatSlideToggle } from "@angular/material/slide-toggle";
import {
  AccountMarketplace,
  MatchType,
  Response,
  Strategy,
  StrategyApi,
  StrategyType,
  Targeting,
} from "@front/m19-api-client";
import { StrategyEx, StrategyGroupEx, StrategyTargetingType } from "@front/m19-models";
import { Constant, SpStrategiesService } from "@front/m19-services";
import {
  ConfirmPopupComponent,
  IBadgeComponent,
  IButtonComponent,
  IInputComponent,
  MODAL_DATA,
  ModalRef,
  ModalService,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { AsinItemComponent } from "@m19-board/shared/asin-item/asin-item.component";
import { AsinSelectionComponent } from "@m19-board/shared/asin-selection/asin-selection.component";
import { ICON_CLOSE, ICON_EDIT_O, ICON_SEARCH } from "@m19-board/utils/iconsLabels";
import { Observable, of, Subject, switchMap } from "rxjs";
import {
  FromBrandSelectionModalComponent,
  FromBrandSelectionModalData,
  SpGroupTargetings,
} from "../sp-substrategy-creation/from-brand-selection-modal/from-brand-selection-modal.component";
import {
  StrategyKeywordModalComponent,
  StrategyKeywordModalData,
  StrategyKeywordModalOutput,
  StrategyKeywordModalResult,
} from "./strategy-keyword-modal.component";

export interface TargetingsModalData {
  strategy: Strategy;
  strategyType: StrategyType;
  targetingType: StrategyTargetingType;
  strategyGroup: StrategyGroupEx;
  accountMarketplace: AccountMarketplace;
  targetings: Targeting[];
  isReadOnly?: boolean;
}

@Component({
  selector: "app-targetings-modal",
  standalone: true,
  imports: [
    MatSlideToggle,
    IButtonComponent,
    IBadgeComponent,
    IInputComponent,
    AsinItemComponent,
    NgClass,
    TranslocoDirective,
    AsinSelectionComponent,
  ],
  templateUrl: "./targetings-modal.component.html",
})
export class TargetingsModalComponent {
  readonly modalRef = inject(ModalRef);
  readonly modalService = inject(ModalService);
  readonly toastrService = inject(ToasterService);
  readonly strategyApi = inject(StrategyApi);
  readonly spStrategiesService = inject(SpStrategiesService);

  readonly ICON_CLOSE = ICON_CLOSE;
  readonly ICON_EDIT = ICON_EDIT_O;
  readonly ICON_SEARCH = ICON_SEARCH;

  readonly MAX_STRATEGY_ASINS = Constant.maxAsinTargetingByStrategy;

  readonly modalData = inject(MODAL_DATA) as TargetingsModalData;

  @ViewChild("productTargetingsModal", { static: false }) productTargetingsModal!: TemplateRef<any>;
  advertisedProductsModalRef?: ModalRef<any, any>;

  searchKeywordQuery = signal<string>("");
  searchProductQuery = signal<string>("");

  keywords = signal<Targeting[]>(this.modalData.targetings.filter((t) => t.matchType !== MatchType.asinSameAs));
  products = signal<Targeting[]>(this.modalData.targetings.filter((t) => t.matchType === MatchType.asinSameAs));
  productTargetings = computed(() => this.products().map((t) => t.targetingValue));
  allTargetings = computed(() => [...this.keywords(), ...this.products()]);

  newProductTargetings = signal<string[]>([]);

  shownKeywords = computed(() => {
    const regex = new RegExp(this.searchKeywordQuery(), "i");
    return this.keywords().filter((t) => regex.test(t.targetingValue));
  });
  shownProducts = computed(() => {
    const regex = new RegExp(this.searchProductQuery(), "i");
    return this.products().filter((t) => regex.test(t.targetingValue));
  });

  constructor(private translocoService: TranslocoService) {}

  // From Brand imports

  toggleTargetingType() {
    if (this.allTargetings().length > 0) {
      const modalRef = this.modalService.openModal(ConfirmPopupComponent, {
        modalTitle: `Change to ${this.modalData.targetingType == StrategyTargetingType.KEYWORDS ? "product" : "keyword"} targeting`,
        data: {
          message: `This will remove the current ${this.allTargetings().length} ${
            this.modalData.targetingType == StrategyTargetingType.KEYWORDS ? "keyword" : "product"
          } targeting${this.allTargetings().length > 1 ? "s" : ""} from the strategy`,
        },
      });
      modalRef
        .afterClosed()
        .pipe(
          switchMap(() =>
            this.spStrategiesService.removeTargetingFromStrategy(
              this.modalData.strategy,
              this.allTargetings(),
              this.modalData.accountMarketplace.resourceOrganizationId!,
            ),
          ),
        )
        .subscribe({
          next: () => {
            this.toastrService.success(
              this.translocoService.translate("targetings-modal.all_targetings_removed_from_strategy"),
              this.translocoService.translate("targetings-modal.strategy_targetings_updated"),
            );
            this.keywords.set([]);
            this.products.set([]);
            this.setTargetingType(
              this.modalData.targetingType === StrategyTargetingType.KEYWORDS
                ? StrategyTargetingType.PRODUCTS
                : StrategyTargetingType.KEYWORDS,
            );
          },
          error: (error: string) => {
            this.toastrService.error(
              this.translocoService.translate("targetings-modal.error_removing_strategy_targetings_error", [error]),
              this.translocoService.translate("targetings-modal.strategy_targetings_update_error"),
            );
          },
        });
    } else {
      this.setTargetingType(
        this.modalData.targetingType === StrategyTargetingType.KEYWORDS
          ? StrategyTargetingType.PRODUCTS
          : StrategyTargetingType.KEYWORDS,
      );
    }
  }

  private setTargetingType(type: StrategyTargetingType) {
    this.modalData.targetingType = type;
  }

  updateIsolation(isolation: boolean) {
    this.strategyApi
      .updateStrategy({
        accountId: this.modalData.strategy.accountId,
        marketplace: this.modalData.strategy.marketplace,
        strategyId: this.modalData.strategy.strategyId!,
        organizationId: this.modalData.accountMarketplace.resourceOrganizationId!,
        asinIsolation: isolation,
      })
      .subscribe({
        next: (res: Response) => {
          const updatedStrategy = res.entity as StrategyEx;
          this.toastrService.success(
            isolation
              ? this.translocoService.translate("targetings-modal.enable_asin_level_isolation_for_strategy", [
                  updatedStrategy.name ?? "",
                ])
              : this.translocoService.translate("targetings-modal.disabled_asin_level_isolation_for_strategy"),
            this.translocoService.translate("targetings-modal.asin_level_isolation_update"),
          );
          this.modalRef.close(updatedStrategy);
        },
        error: (e: string) => {
          this.toastrService.error(
            e,
            this.translocoService.translate("targetings-modal.strategy_isolation_update_error"),
          );
        },
      });
  }

  editKeywordTargetings() {
    const ref = this.modalService.openModal<StrategyKeywordModalData, StrategyKeywordModalOutput>(
      StrategyKeywordModalComponent,
      {
        modalTitle: this.translocoService.translate("targetings-modal.edit_keyword_targetings"),
        data: {
          marketplace: this.modalData.accountMarketplace.marketplace,
          keywords: this.keywords(),
          isReadOnly: !!this.modalData.isReadOnly,
        },
      },
    );

    ref
      .afterClosed()
      .pipe(
        switchMap((result) => {
          if (result.result === StrategyKeywordModalResult.ADDED_KEYWORDS) {
            return this.addKeywords(result.keywords);
          } else if (result.result === StrategyKeywordModalResult.DELETED_KEYWORDS) {
            return this.deleteKeywords(result.keywords);
          }
          return of(void 0);
        }),
      )
      .subscribe({
        next: () => {
          this.toastrService.success(this.translocoService.translate("targetings-modal.strategy_targeting_updated"));
        },
        error: (error: string) => {
          this.toastrService.error(
            this.translocoService.translate("targetings-modal.error_adding_strategy_keyword", [error]),
            this.translocoService.translate("targetings-modal.strategy_targeting_update_error"),
          );
        },
      });
  }

  private addKeywords(targetings: Targeting[]): Observable<void> {
    const filteredTargetings = targetings.filter(
      (t) => !this.keywords().find((a) => a.targetingValue == t.targetingValue),
    );

    if (filteredTargetings.length == 0) {
      this.toastrService.info(this.translocoService.translate("targetings-modal.no_new_keywords_to_add"));
      return of(void 0);
    }
    const result = new Subject<void>();
    this.spStrategiesService
      .addTargetingToStrategy(
        this.modalData.strategy,
        filteredTargetings,
        this.modalData.accountMarketplace.resourceOrganizationId!,
      )
      .subscribe({
        next: () => {
          this.toastrService.success(
            this.translocoService.translate("targetings-modal.keyword_s_added_to_strategy"),
            this.translocoService.translate("targetings-modal.strategy_targeting_updated"),
          );
          this.keywords.set([...this.keywords(), ...filteredTargetings]);
          result.next();
          result.complete();
        },
        error: (error: string) => {
          this.toastrService.error(
            this.translocoService.translate("targetings-modal.error_adding_strategy_keyword", [error]),
            this.translocoService.translate("targetings-modal.strategy_targeting_update_error"),
          );
          result.next();
          result.complete();
        },
      });
    return result;
  }

  private deleteKeywords(targetings: Targeting[]): Observable<void> {
    const result = new Subject<void>();
    this.spStrategiesService
      .removeTargetingFromStrategy(
        this.modalData.strategy,
        targetings,
        this.modalData.accountMarketplace.resourceOrganizationId!,
      )
      .subscribe({
        next: () => {
          this.toastrService.success(
            this.translocoService.translate("targetings-modal.keyword_s_removed_from_strategy"),
            this.translocoService.translate("targetings-modal.strategy_targeting_updated"),
          );
          this.keywords.set(
            this.keywords().filter(
              (t) =>
                targetings.findIndex((k) => k.matchType === t.matchType && k.targetingValue === t.targetingValue) < 0,
            ),
          );
          result.next();
          result.complete();
        },
        error: (error: string) => {
          this.toastrService.error(
            this.translocoService.translate("targetings-modal.error_removing_strategy_keyword"),
            this.translocoService.translate("targetings-modal.strategy_targeting_update_error"),
          );
          result.next();
          result.complete();
        },
      });
    return result;
  }

  editProductTargetings() {
    this.advertisedProductsModalRef = this.modalService.openModal(this.productTargetingsModal!, {});
  }

  updateProductTargetings(targetings: string[]) {
    if (targetings.length == 0) {
      return;
    } else {
      const toAdd = targetings.filter(
        (a) =>
          !this.products()
            .map((t) => t.targetingValue)
            .includes(a),
      );
      const toRemove = this.products()
        .map((t) => t.targetingValue)
        .filter((a) => !targetings.includes(a));

      if (toAdd.length > 0) {
        this.addProduct(toAdd);
      }
      if (toRemove.length > 0) {
        this.deleteProduct(toRemove);
      }
      this.newProductTargetings.set([]);
      this.advertisedProductsModalRef?.close();
    }
  }

  private addProduct(asins: string[]) {
    if (asins.length == 0) {
      return;
    }
    const filtered: Targeting[] = asins.map((a) => ({ targetingValue: a, matchType: MatchType.asinSameAs }));

    this.spStrategiesService
      .addTargetingToStrategy(
        this.modalData.strategy,
        filtered,
        this.modalData.accountMarketplace.resourceOrganizationId!,
      )
      .subscribe({
        next: () => {
          this.toastrService.success(
            this.translocoService.translate("targetings-modal.product_targeting_s_added_to_strategy"),
            this.translocoService.translate("targetings-modal.strategy_targeting_updated"),
          );
          this.products.set([...this.products(), ...filtered]);
          this.modalData.strategy.targetings = [...this.keywords(), ...this.products()];
        },
        error: (error: string) => {
          this.toastrService.error(
            this.translocoService.translate("targetings-modal.error_adding_strategy_product_targeting_s_error", [
              error,
            ]),
            this.translocoService.translate("targetings-modal.strategy_targeting_update_error"),
          );
        },
      });
  }

  private deleteProduct(asins: string[]) {
    const targetings: Targeting[] = asins.map((a) => ({ targetingValue: a, matchType: MatchType.asinSameAs }));

    this.spStrategiesService
      .removeTargetingFromStrategy(
        this.modalData.strategy,
        targetings,
        this.modalData.accountMarketplace.resourceOrganizationId!,
      )
      .subscribe({
        next: () => {
          this.toastrService.success(
            this.translocoService.translate("targetings-modal.product_targeting_s_removed_from_strategy"),
            this.translocoService.translate("targetings-modal.strategy_targeting_updated"),
          );
          this.products.update((products) =>
            products.filter((a) => !targetings.map((t) => t.targetingValue).includes(a.targetingValue)),
          );
          this.modalData.strategy.targetings = [...this.keywords(), ...this.products()];
        },
        error: (error: string) => {
          this.toastrService.error(
            this.translocoService.translate("targetings-modal.error_removing_strategy_product_targeting_s_error", [
              error,
            ]),
            this.translocoService.translate("targetings-modal.strategy_targeting_update_error"),
          );
        },
      });
  }

  addTargetFromBrand() {
    const ref = this.modalService.openModal<FromBrandSelectionModalData, SpGroupTargetings>(
      FromBrandSelectionModalComponent,
      {},
    );

    ref.afterClosed().subscribe((targetings) => {
      if (targetings) {
        this.addKeywords(targetings.keywords);
        this.addProduct(targetings.products.map((t) => t.asin));
      }
    });
  }

  addTargetingsFromBrand() {
    throw new Error("Not implemented");
  }

  readonly StrategyTargetingType = StrategyTargetingType;
  readonly MatchType = MatchType;
  readonly StrategyType = StrategyType;
}
